import { z } from 'zod'

import type { ZodInfer } from '@mntn-dev/utility-types'

import { ModelFilterSchema } from '../model-filter-schema.ts'
import { BillingScheduleSchema } from './billing/billing-schedule.ts'
import { CurrencySchema } from './billing/currency.ts'
import { InvoiceBillingTermsSchema } from './billing/invoice-terms.ts'
import { ExternalChargeSchema } from './billing.models.ts'
import {
  BillingServiceIdSchema,
  BillingServiceMethodIdSchema,
} from './billing-service.models.ts'
import { UbsEntityIdSchema } from './finance/ubs-entity-id.ts'
import {
  EmailAddressSchema,
  NameSchema,
  NonEmptyStringSchema,
} from './property.models.ts'
import {
  BillingProfileIdSchema,
  NetsuiteIdSchema,
  OrganizationIdSchema,
} from './unique-id.models.ts'

export const BillingAddressSchema = NonEmptyStringSchema(256)
export type BillingAddress = ZodInfer<typeof BillingAddressSchema>

/**
 * BillingProfileDomainSelectModel
 */

export const BillingProfileNameSchema = NameSchema
export type BillingProfileName = ZodInfer<typeof BillingProfileNameSchema>

export const BillingProfileDomainSelectModelSchema = z.object({
  billingProfileId: BillingProfileIdSchema,
  name: BillingProfileNameSchema,
  netsuiteId: NetsuiteIdSchema,
  organizationId: OrganizationIdSchema,
  billingServiceId: BillingServiceIdSchema.optional(),
  billingServiceMethodId: BillingServiceMethodIdSchema.optional(),
  billingSchedule: BillingScheduleSchema,
  hasBillingAddress: z.boolean(),
  billingContactEmail: EmailAddressSchema.optional(),
  currency: CurrencySchema,
  invoiceTerms: InvoiceBillingTermsSchema,
  failedCharges: z.array(ExternalChargeSchema).optional(),
  ams: z.boolean(),
  ubsEntityId: UbsEntityIdSchema.optional(),
})

export type BillingProfileDomainSelectModel = ZodInfer<
  typeof BillingProfileDomainSelectModelSchema
>

/**
 * BillingProfileDomainInsertModel
 */

export const BillingProfileDomainInsertModelSchema =
  BillingProfileDomainSelectModelSchema.partial({
    billingProfileId: true,
    netsuiteId: true,
  })

export type BillingProfileDomainInsertModel = ZodInfer<
  typeof BillingProfileDomainInsertModelSchema
>

/**
 * BillingProfileDomainUpdateModel
 */

export const BillingProfileDomainUpdateModelSchema =
  BillingProfileDomainSelectModelSchema.pick({
    netsuiteId: true,
    billingServiceId: true,
    billingServiceMethodId: true,
    billingContactEmail: true,
    hasBillingAddress: true,
    billingSchedule: true,
    currency: true,
    invoiceTerms: true,
    failedCharges: true,
    ams: true,
    name: true,
    ubsEntityId: true,
  })
    .extend({
      billingContactEmail: EmailAddressSchema.nullable(),
      billingServiceMethodId: BillingServiceMethodIdSchema.nullable(),
    })
    .partial()

export type BillingProfileDomainUpdateModel = ZodInfer<
  typeof BillingProfileDomainUpdateModelSchema
>

/**
 * BillingProfileDomainSelectModelFilters
 */
export const BillingProfileDomainSelectModelFiltersSchema = z
  .object({
    billingProfileId: ModelFilterSchema(BillingProfileIdSchema),
    organizationId: ModelFilterSchema(OrganizationIdSchema),
    netsuiteId: ModelFilterSchema(NetsuiteIdSchema),
    billingServiceId: ModelFilterSchema(BillingServiceIdSchema),
    billingSchedule: ModelFilterSchema(BillingScheduleSchema),
    currency: ModelFilterSchema(CurrencySchema),
    invoiceTerms: ModelFilterSchema(InvoiceBillingTermsSchema),
    ams: z.boolean(),
    name: ModelFilterSchema(NameSchema.toLowerCase()),
    needsPopulating: z.boolean(),
  })
  .partial()

export type BillingProfileDomainSelectModelFilters = ZodInfer<
  typeof BillingProfileDomainSelectModelFiltersSchema
>
